import { Card, Stack, Button, Typography, Box, Grid } from "@mui/material";
import { useEffect, useState, useCallback } from "react";
import { get, put } from "src/utils/networkUtils";
import Iconify from "../iconify";
import PlayerLineup from "./PlayerLineup";
import PlaygroundUserSearch from "./PlaygroundUserSearch";
import DailyStreak from "./DailyStreak";
import VersusLineupBoost from "./VersusLineupBoost";
import UserCardsDialog from "./UserCardsDialog";
import { getLineupFpts } from "./utils";
import { fDateTime } from "src/utils/formatTime";

export default function UserLineupCard({
  lineup = [],
  dailyStreak,
  loading,
  setLoading,
  selectedUser,
  username,
  setLineup,
  setSelectedUser,
}) {
  const [showSearch, setShowSearch] = useState(false);
  const [showCardsDialog, setShowCardsDialog] = useState(false);
  const [cardPositionFilter, setCardPositionFilter] = useState(null);
  const [cardToReplace, setCardToReplace] = useState(null);
  const [isReplacingCard, setIsReplacingCard] = useState(false);

  const fetchUserLineup = useCallback(
    (userId) => {
      setSelectedUser(userId);
      get(`/admin/versus/users/${userId}/lineup`)
        .then(function (response) {
          setLineup(response.lineup || []);
        })
        .catch(function (error) {
          console.error("Error fetching user lineup:", error);
          setLineup([]);
        })
        .finally(() => {
          setShowSearch(false);
        });
    },
    [setLineup, setSelectedUser, setShowSearch]
  );

  useEffect(() => {
    if (selectedUser) {
      fetchUserLineup(selectedUser);
    }
  }, [selectedUser, fetchUserLineup]);

  const handleUpdateRandom = () => {
    if (!selectedUser) return;
    setLoading(true);
    put(`/admin/versus/users/${selectedUser}/random-lineup`)
      .then(function (response) {
        setLineup(response.lineup || []);
        setLoading(false);
      })
      .catch(function (error) {
        console.error("Error creating random lineup:", error);
        setLoading(false);
      });
  };

  const handleUserSelect = (userIds) => {
    if (userIds && userIds.length > 0) {
      fetchUserLineup(userIds[0]);
    }
  };

  const handleCardClick = (card) => {
    setCardPositionFilter(card.position);
    setCardToReplace(card);
    setShowCardsDialog(true);
  };

  const handleCardsDialogClose = () => {
    setShowCardsDialog(false);
    setCardPositionFilter(null);
    setCardToReplace(null);
    setIsReplacingCard(false);
  };
  const handleCardReplacement = async (newCard) => {
    if (!cardToReplace || !selectedUser || isReplacingCard) return;

    setIsReplacingCard(true);

    const newLineup = lineup.map((lineupCard) => {
      if (lineupCard.cardId === cardToReplace.cardId) {
        return newCard;
      }
      return lineupCard;
    });

    try {
      const cardIds = newLineup.map((card) => card.cardId);

      const response = await put(`/admin/versus/users/${selectedUser}/lineup`, {
        cardIds: cardIds,
      });

      if (response.success || response.lineup) {
        setLineup(response.lineup || newLineup);
        console.log("Lineup updated successfully");
      } else {
        console.error("Failed to update lineup:", response.error);
      }
    } catch (error) {
      console.error("Error updating lineup:", error);

      setLineup(newLineup);
    } finally {
      setIsReplacingCard(false);
    }

    handleCardsDialogClose();
  };

  return (
    <Card sx={{ mb: 3, boxShadow: 2, p: 2, minHeight: 300 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Typography variant="h6" gutterBottom>
          {username ? `User: ${username}` : "Select a User"}
        </Typography>
        {lineup && lineup.length > 0 && (
          <Typography variant="h6" gutterBottom>
            FPTS: {getLineupFpts(lineup)}
          </Typography>
        )}
        <Box>
          {selectedUser && (
            <Button
              variant="outlined"
              startIcon={<Iconify icon="eva:flip-outline" />}
              onClick={handleUpdateRandom}
              sx={{ mr: 1, mb: 1 }}
            >
              Random Lineup
            </Button>
          )}

          <Button
            variant="contained"
            startIcon={
              showSearch ? (
                <Iconify icon="eva:close-outline" />
              ) : (
                <Iconify icon="eva:search-outline" />
              )
            }
            onClick={() => setShowSearch(!showSearch)}
            sx={{ mb: 1 }}
          >
            {showSearch ? "Close Search" : "Search User"}
          </Button>
        </Box>
      </Stack>

      {showSearch && <PlaygroundUserSearch onUserSelect={handleUserSelect} />}

      {selectedUser && (
        <PlayerLineup
          lineup={lineup}
          loading={loading}
          onCardClick={handleCardClick}
        />
      )}
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12} md={4}>
          {lineup && lineup.length > 0 && <VersusLineupBoost lineup={lineup} />}
        </Grid>
        <Grid item xs={12} md={8}>
          {selectedUser && dailyStreak && (
            <DailyStreak
              currentStreak={
                dailyStreak.dailyWins > dailyStreak.overallGamesCount
                  ? dailyStreak.overallGamesCount
                  : dailyStreak.dailyWins
              }
              totalDays={dailyStreak.overallGamesCount}
              timeRemaining={fDateTime(dailyStreak.endDate)}
            />
          )}
        </Grid>
      </Grid>

      {/* User Cards Dialog */}
      <UserCardsDialog
        open={showCardsDialog}
        onClose={handleCardsDialogClose}
        userId={selectedUser}
        username={username}
        positionFilter={cardPositionFilter}
        onCardClick={cardToReplace ? handleCardReplacement : undefined}
        isReplacingCard={isReplacingCard}
      />
    </Card>
  );
}
